import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';

// Itens de navegação principais. Seções futuras (Oportunidades, Aprenda,
// Arcade) entram aqui quando suas páginas existirem — nunca antes.
const NAV_ITEMS: { label: string; to: string; highlight?: boolean }[] = [
  { label: 'Início', to: '/' },
  { label: 'Resumo Diário', to: '/resumo-diario' },
  { label: 'Oportunidades', to: '/oportunidades' },
  { label: 'Ferramentas', to: '/ferramentas', highlight: true },
  { label: 'Aprenda', to: '/aprenda' },
  { label: 'Arcade', to: '/arcade' },
  { label: 'Glossário', to: '/glossario' },
];

export const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const siteName = import.meta.env.VITE_SITE_NAME || 'Cripto News';

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const term = searchTerm.trim();
    if (!term) return;
    navigate(`/?busca=${encodeURIComponent(term)}`);
    setSearchTerm('');
    setIsMenuOpen(false);
  };

  return (
    <header className="bg-gradient-to-r from-blue-600 to-blue-800 text-white shadow-lg">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 flex-shrink-0">
            <div className="text-2xl font-bold">
              <span className="text-yellow-400">₿</span> {siteName}
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-5">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={
                  item.highlight
                    ? 'hover:text-yellow-400 transition font-semibold text-yellow-300 whitespace-nowrap'
                    : 'hover:text-yellow-400 transition whitespace-nowrap'
                }
              >
                {item.highlight ? `⚡ ${item.label}` : item.label}
              </Link>
            ))}
          </nav>

          {/* Search (desktop) */}
          <form onSubmit={handleSearch} className="hidden md:flex items-center flex-shrink min-w-0">
            <input
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar notícias..."
              aria-label="Buscar notícias"
              className="w-40 lg:w-48 bg-blue-900/40 border border-blue-400/40 rounded-l-lg px-3 py-1.5 text-sm placeholder-blue-200 focus:outline-none focus:border-yellow-400"
            />
            <button
              type="submit"
              aria-label="Buscar"
              className="bg-yellow-400 text-blue-900 rounded-r-lg px-3 py-1.5 hover:bg-yellow-300 transition"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
              </svg>
            </button>
          </form>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2"
            aria-label="Abrir menu"
            aria-expanded={isMenuOpen}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="lg:hidden py-4 space-y-2">
            <form onSubmit={handleSearch} className="flex items-center mb-3 md:hidden">
              <input
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar notícias..."
                aria-label="Buscar notícias"
                className="flex-grow bg-blue-900/40 border border-blue-400/40 rounded-l-lg px-3 py-2 text-sm placeholder-blue-200 focus:outline-none focus:border-yellow-400"
              />
              <button
                type="submit"
                aria-label="Buscar"
                className="bg-yellow-400 text-blue-900 rounded-r-lg px-4 py-2 hover:bg-yellow-300 transition"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
                </svg>
              </button>
            </form>
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={
                  item.highlight
                    ? 'block py-2 hover:text-yellow-400 transition font-semibold text-yellow-300'
                    : 'block py-2 hover:text-yellow-400 transition'
                }
                onClick={() => setIsMenuOpen(false)}
              >
                {item.highlight ? `⚡ ${item.label}` : item.label}
              </Link>
            ))}
          </nav>
        )}
      </div>
    </header>
  );
};
